import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, PawPrint, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';

const APP_URL = "https://bowwow-dogpark.emergent.host/";

// Logo Component matching the app
const Logo = ({ size = "default" }) => {
  const sizeClasses = size === "large" ? "w-14 h-14" : "w-10 h-10";
  const iconSize = size === "large" ? "w-8 h-8" : "w-6 h-6";
  
  return (
    <div className={`${sizeClasses} rounded-full bg-gradient-to-br from-[#2A9DB0] to-[#FF9500] flex items-center justify-center shadow-md`}>
      <div className={`${iconSize} text-white`}>
        <svg viewBox="0 0 24 24" fill="currentColor">
          <circle cx="8" cy="9" r="1.5" />
          <circle cx="16" cy="9" r="1.5" />
          <circle cx="12" cy="14" r="2" />
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fillOpacity="0.3"/>
        </svg>
      </div>
    </div>
  );
};

const PartnerQuestionnaire = () => {
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    phone: '',
    email: '',
    livesInApartment: '',
    apartmentBuildingsNearby: '',
    hasOwnPets: '',
    ownPetTypes: [],
    otherPetType: '',
    ownPetCount: '',
    services: [],
    hasPetExperience: '',
    hasReliableTransportation: '',
    canReferOthers: '',
    canPassBackgroundCheck: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const serviceOptions = [
    { id: 'dog-walking', label: 'Dog Walking' },
    { id: 'pet-sitting', label: 'Pet Sitting' },
    { id: 'overnight-care', label: 'Overnight Care' },
    { id: 'drop-in-visits', label: 'Drop-in Visits' },
    { id: 'transport', label: 'Transport' },
    { id: 'concierge', label: 'Concierge (if you can think of it, we just might do it!)' }
  ];

  const petTypeOptions = [
    { id: 'dog', label: 'Dog' },
    { id: 'cat', label: 'Cat' },
    { id: 'bunny', label: 'Bunny' },
    { id: 'bird', label: 'Bird' },
    { id: 'other', label: 'Other' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRadioChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePetTypeChange = (petId, checked) => {
    setFormData(prev => ({
      ...prev,
      ownPetTypes: checked 
        ? [...prev.ownPetTypes, petId]
        : prev.ownPetTypes.filter(id => id !== petId)
    }));
  };

  const handleServiceChange = (serviceId, checked) => {
    setFormData(prev => ({
      ...prev,
      services: checked 
        ? [...prev.services, serviceId]
        : prev.services.filter(id => id !== serviceId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.livesInApartment) {
      toast.error('Please answer the apartment question');
      return;
    }

    if (formData.services.length === 0) {
      toast.error('Please select at least one service you can provide');
      return;
    }

    if (!formData.hasPetExperience) {
      toast.error('Please answer the pet experience question');
      return;
    }

    if (!formData.canPassBackgroundCheck) {
      toast.error('Please answer the background check question');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Thank you! We received your application.');
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToRegistration = () => {
    // Redirect to the main app registration with provider-only option
    window.open(`${APP_URL}?role=provider`, '_blank');
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center px-6">
        <Card className="max-w-lg w-full rounded-3xl shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-[#4CD964] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-fredoka text-2xl font-bold text-accent-charcoal mb-4">
              Thank You!
            </h2>
            <p className="text-neutral-muted mb-8">
              We've received your application and will be in touch soon. 
              Complete your registration to become a Partner-Provider!
            </p>
            <Button 
              onClick={goToRegistration}
              className="bg-[#2A9DB0] hover:bg-[#238a9a] text-white rounded-full px-8 py-6 text-lg font-bold hover:scale-105 transition-transform"
              data-testid="complete-registration-btn"
            >
              Complete Registration
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3]" data-testid="partner-questionnaire-page">
      {/* Header */}
      <header className="bg-[#FDF8F3]/95 backdrop-blur-md border-b border-neutral-border py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <Logo />
            <span className="font-fredoka text-2xl font-bold text-accent-charcoal">
              BowWowMeow
            </span>
          </a>
          <a href="/" className="flex items-center gap-2 text-neutral-muted hover:text-[#2A9DB0] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Home</span>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Title */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#2A9DB0]/10 rounded-full px-4 py-2 mb-4">
              <PawPrint className="w-4 h-4 text-[#2A9DB0]" />
              <span className="text-sm font-medium text-[#2A9DB0]">Become a Partner-Provider</span>
            </div>
            <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-accent-charcoal mb-4">
              Join Our Team of Pet Lovers
            </h1>
            <p className="text-neutral-muted">
              Tell us about yourself and start earning by caring for furry friends in your area.
            </p>
          </div>

          {/* Form */}
          <Card className="rounded-3xl shadow-lg">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Contact Information */}
                <div>
                  <h3 className="font-fredoka text-xl font-bold text-accent-charcoal mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-[#2A9DB0] rounded-lg flex items-center justify-center text-white text-sm">1</span>
                    Your Information
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-accent-charcoal font-medium">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="rounded-xl border-neutral-border"
                        data-testid="input-name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city" className="text-accent-charcoal font-medium">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        type="text"
                        placeholder="Your city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="rounded-xl border-neutral-border"
                        data-testid="input-city"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-accent-charcoal font-medium">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="Your phone number"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="rounded-xl border-neutral-border"
                        data-testid="input-phone"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-accent-charcoal font-medium">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Your email address"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="rounded-xl border-neutral-border"
                        data-testid="input-email"
                        required
                      />
                    </div>
                  </div>

                  {/* Housing Questions */}
                  <div className="mt-6 space-y-4">
                    <div>
                      <Label className="text-accent-charcoal font-medium mb-3 block">
                        Do you live in an apartment complex/building? *
                      </Label>
                      <RadioGroup
                        value={formData.livesInApartment}
                        onValueChange={(value) => handleRadioChange('livesInApartment', value)}
                        className="flex gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="apartment-yes" data-testid="radio-apartment-yes" />
                          <Label htmlFor="apartment-yes" className="cursor-pointer">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id="apartment-no" data-testid="radio-apartment-no" />
                          <Label htmlFor="apartment-no" className="cursor-pointer">No</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="apartmentBuildingsNearby" className="text-accent-charcoal font-medium">
                        How many apartment buildings of 50 or more units are within 1 mile of your residence?
                      </Label>
                      <Input
                        id="apartmentBuildingsNearby"
                        name="apartmentBuildingsNearby"
                        type="number"
                        min="0"
                        placeholder="Number of buildings"
                        value={formData.apartmentBuildingsNearby}
                        onChange={handleInputChange}
                        className="rounded-xl border-neutral-border w-full sm:w-48"
                        data-testid="input-apartment-buildings"
                      />
                    </div>

                    {/* Pet Ownership Questions */}
                    <div className="mt-6 pt-6 border-t border-neutral-border">
                      <div>
                        <Label className="text-accent-charcoal font-medium mb-3 block">
                          Do you have any pets?
                        </Label>
                        <RadioGroup
                          value={formData.hasOwnPets}
                          onValueChange={(value) => handleRadioChange('hasOwnPets', value)}
                          className="flex gap-6"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="yes" id="own-pets-yes" data-testid="radio-own-pets-yes" />
                            <Label htmlFor="own-pets-yes" className="cursor-pointer">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="no" id="own-pets-no" data-testid="radio-own-pets-no" />
                            <Label htmlFor="own-pets-no" className="cursor-pointer">No</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {formData.hasOwnPets === 'yes' && (
                        <div className="mt-4 space-y-4">
                          <div>
                            <Label className="text-accent-charcoal font-medium mb-3 block">What type of pet(s)?</Label>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                              {petTypeOptions.map((pet) => (
                                <div key={pet.id} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`own-pet-${pet.id}`}
                                    checked={formData.ownPetTypes.includes(pet.id)}
                                    onCheckedChange={(checked) => handlePetTypeChange(pet.id, checked)}
                                    data-testid={`checkbox-own-pet-${pet.id}`}
                                  />
                                  <Label 
                                    htmlFor={`own-pet-${pet.id}`} 
                                    className="text-sm text-neutral-muted cursor-pointer"
                                  >
                                    {pet.label}
                                  </Label>
                                </div>
                              ))}
                            </div>
                            
                            {formData.ownPetTypes.includes('other') && (
                              <div className="mt-3">
                                <Input
                                  name="otherPetType"
                                  type="text"
                                  placeholder="Please specify your pet type"
                                  value={formData.otherPetType}
                                  onChange={handleInputChange}
                                  className="rounded-xl border-neutral-border"
                                  data-testid="input-other-pet-type"
                                />
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="ownPetCount" className="text-accent-charcoal font-medium">How many pets do you have?</Label>
                            <Input
                              id="ownPetCount"
                              name="ownPetCount"
                              type="number"
                              min="1"
                              placeholder="Number of pets"
                              value={formData.ownPetCount}
                              onChange={handleInputChange}
                              className="rounded-xl border-neutral-border w-full sm:w-48"
                              data-testid="input-own-pet-count"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Services */}
                <div>
                  <h3 className="font-fredoka text-xl font-bold text-accent-charcoal mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-[#FF9500] rounded-lg flex items-center justify-center text-white text-sm">2</span>
                    Services You Can Provide
                  </h3>
                  <div className="space-y-3">
                    {serviceOptions.map((service) => (
                      <div 
                        key={service.id} 
                        className={`flex items-center space-x-3 p-3 rounded-xl border transition-colors cursor-pointer ${
                          formData.services.includes(service.id) 
                            ? 'border-[#2A9DB0] bg-[#2A9DB0]/5' 
                            : 'border-neutral-border hover:border-[#2A9DB0]/50'
                        }`}
                        onClick={() => handleServiceChange(service.id, !formData.services.includes(service.id))}
                      >
                        <Checkbox
                          id={`service-${service.id}`}
                          checked={formData.services.includes(service.id)}
                          onCheckedChange={(checked) => handleServiceChange(service.id, checked)}
                          data-testid={`checkbox-service-${service.id}`}
                        />
                        <Label 
                          htmlFor={`service-${service.id}`} 
                          className="text-accent-charcoal cursor-pointer flex-1"
                        >
                          {service.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Experience & Background */}
                <div>
                  <h3 className="font-fredoka text-xl font-bold text-accent-charcoal mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-[#4CD964] rounded-lg flex items-center justify-center text-white text-sm">3</span>
                    Experience & Background
                  </h3>
                  <div className="space-y-6">
                    <div>
                      <Label className="text-accent-charcoal font-medium mb-3 block">
                        Do you have pet service experience? *
                      </Label>
                      <RadioGroup
                        value={formData.hasPetExperience}
                        onValueChange={(value) => handleRadioChange('hasPetExperience', value)}
                        className="flex gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="experience-yes" data-testid="radio-experience-yes" />
                          <Label htmlFor="experience-yes" className="cursor-pointer">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id="experience-no" data-testid="radio-experience-no" />
                          <Label htmlFor="experience-no" className="cursor-pointer">No</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div>
                      <Label className="text-accent-charcoal font-medium mb-3 block">
                        Do you have reliable transportation? *
                      </Label>
                      <RadioGroup
                        value={formData.hasReliableTransportation}
                        onValueChange={(value) => handleRadioChange('hasReliableTransportation', value)}
                        className="flex gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="transportation-yes" data-testid="radio-transportation-yes" />
                          <Label htmlFor="transportation-yes" className="cursor-pointer">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id="transportation-no" data-testid="radio-transportation-no" />
                          <Label htmlFor="transportation-no" className="cursor-pointer">No</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div>
                      <Label className="text-accent-charcoal font-medium mb-3 block">
                        Can you refer others to join the team? *
                      </Label>
                      <RadioGroup
                        value={formData.canReferOthers}
                        onValueChange={(value) => handleRadioChange('canReferOthers', value)}
                        className="flex gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="refer-yes" data-testid="radio-refer-yes" />
                          <Label htmlFor="refer-yes" className="cursor-pointer">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id="refer-no" data-testid="radio-refer-no" />
                          <Label htmlFor="refer-no" className="cursor-pointer">No</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div>
                      <Label className="text-accent-charcoal font-medium mb-3 block">
                        Can you pass a background check? *
                      </Label>
                      <RadioGroup
                        value={formData.canPassBackgroundCheck}
                        onValueChange={(value) => handleRadioChange('canPassBackgroundCheck', value)}
                        className="flex gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="background-yes" data-testid="radio-background-yes" />
                          <Label htmlFor="background-yes" className="cursor-pointer">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id="background-no" data-testid="radio-background-no" />
                          <Label htmlFor="background-no" className="cursor-pointer">No</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#2A9DB0] hover:bg-[#238a9a] text-white rounded-full py-6 text-lg font-bold hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-questionnaire-btn"
                  >
                    {isSubmitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        Become a Partner-Provider
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                  <p className="text-center text-sm text-neutral-muted mt-4">
                    By submitting, you agree to our Terms of Service and Privacy Policy.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default PartnerQuestionnaire;
